import { Controller } from "@hotwired/stimulus"

// ui--facilities--location-cascade — filters a chain of location dropdowns
// (Site → Building → Zone → optional AssetGroup) so a change at any level
// narrows the options of every level below it. Children are disabled until
// their parent has a value.
//
// Each level is a wrapper `<div>` carrying:
//   data-ui--facilities--location-cascade-target="level"
//   data-full-options='[{value, text, parent_id?}, ...]'   ← source of truth
//
// Change events are wired programmatically on the underlying `<select>`
// in `connect()` — the wrapper is NOT a Stimulus action target. Doing
// both would produce double-dispatch of the cascade per user action
// because Tom Select's change fires on the <select> and bubbles up.
//
// Inside every wrapper is a `Ui::SelectComponent` — its `<select>` gets
// wrapped by Tom Select via `data-controller="ui--select"`. We drive the
// filtering through Tom Select's API (clearOptions / addOption / setValue)
// rather than manipulating the underlying `<option>` DOM, because Tom
// Select renders its dropdown from its own internal option dict and
// ignores post-init changes to the `<option>` tree.
//
// **Cascade propagation** is done via direct recursion in `#filterChildOf`
// (each call to filter level N synchronously calls into N+1). We NEVER
// synthesise `change` events on the wrapper to propagate — that risks
// re-entering the cascade mid-loop and producing overlapping runs. Real
// user-driven changes still hit the programmatic change listener attached
// to each level's underlying `<select>` in `connect()` because Tom Select
// fires a native `change` on that element.
//
// **Legacy handling**: buildings can carry `parent_id: null` mid-backfill
// (they're anchored only via `contact_company_site_id`, per the pack's
// Fork of concerns note in packs/facilities/CLAUDE.md). The filter shows
// them ONLY when the parent has no value picked — matching the strict
// `buildings_for_filter` on zones/index. Under a specific site they are
// hidden, because an unanchored building doesn't legitimately belong to
// any FacilitiesSite. Zones + asset_groups schema-guarantee NOT NULL
// parent columns, so the branch is dead code for those levels.
//
// **Silent-failure fallback**: if Tom Select never initializes on some
// level within the 500ms deadline (JS error, missing dep), we leave the
// native `<select>`s disabled — better a locked-down form than one that
// silently accepts any option regardless of parent.
export default class extends Controller {
  static targets = ["level"]

  connect() {
    this.fullOptions = this.levelTargets.map((el) => {
      try {
        return JSON.parse(el.dataset.fullOptions || "[]")
      } catch (_) {
        return []
      }
    })
    this.#warnOnOversizedPayload()

    // Pre-disable every non-root level's native <select> as a fail-safe:
    // if Tom Select never initializes (JS error, missing dep, network
    // failure), the form stays locked to "pick site first" instead of
    // silently accepting a child value under any parent.
    this.#preDisableChildren()

    // Wire change listeners programmatically on the underlying <select>.
    // We do NOT also use a `data-action="change->…"` on the wrapper — Tom
    // Select's change dispatches on the <select> and bubbles up, so a
    // data-action on the wrapper would fire the SAME event a second time,
    // producing double-dispatch of the cascade per user action. Keeping
    // one listener source keeps the flow deterministic.
    this.#changeHandlers = new Map()
    this.levelTargets.forEach((wrapper, idx) => {
      const select = this.#nativeSelectFor(wrapper)
      if (!select) return
      const handler = () => this.#filterChildOf(idx)
      select.addEventListener("change", handler)
      this.#changeHandlers.set(select, handler)
    })

    this.#whenTomSelectsReady((allReady) => {
      if (!allReady) {
        console.warn(
          "[location-cascade] Tom Select failed to initialize on one or " +
            "more levels within 500ms; locking children via native <select> " +
            "disable + aria-disabled fallback. Children will not filter " +
            "until reload.",
        )
        // Reinforce the disable in case Tom Select's own connect ran
        // AFTER ours and blew away the native disabled flag before
        // failing (Stimulus doesn't guarantee connect() order across
        // nested controllers). Also flip aria-disabled on the native
        // <select> (an interactive control — aria-disabled on the outer
        // wrapper <div> has no defined semantics for assistive tech).
        this.#preDisableChildren()
        for (let i = 1; i < this.levelTargets.length; i++) {
          const select = this.#nativeSelectFor(this.levelTargets[i])
          if (select) select.setAttribute("aria-disabled", "true")
        }
        return
      }
      if (this.levelTargets.length >= 2) {
        this.#filterChildOf(0)
      }
    })
  }

  disconnect() {
    if (!this.#changeHandlers) return
    this.#changeHandlers.forEach((handler, select) => {
      select.removeEventListener("change", handler)
    })
    this.#changeHandlers.clear()
  }

  #changeHandlers
  // Re-entry guard for #filterChildOf. Today every mutation on the child
  // Tom Select is silent (`clear(true)`, `setValue(v, true)`) so the
  // change listener on the child <select> never fires while we're
  // rebuilding it — but the guard makes that invariant explicit and
  // survives future non-silent additions (e.g. disable()/enable() that
  // start firing change in a newer Tom Select version).
  #cascading = false

  #tomSelectFor(wrapper) {
    const host = wrapper.querySelector('[data-controller~="ui--select"]')
    if (!host) return null
    const ctrl = this.application.getControllerForElementAndIdentifier(
      host,
      "ui--select",
    )
    return ctrl?.tomSelect ?? null
  }

  #nativeSelectFor(wrapper) {
    return wrapper.querySelector('select[data-ui--select-target="select"]')
  }

  #preDisableChildren() {
    for (let i = 1; i < this.levelTargets.length; i++) {
      const select = this.#nativeSelectFor(this.levelTargets[i])
      if (select) select.disabled = true
    }
  }

  #warnOnOversizedPayload() {
    const CAP = 500
    this.fullOptions.forEach((options, i) => {
      if (options.length > CAP) {
        console.warn(
          `[location-cascade] level ${i} payload has ${options.length} ` +
            `rows (>${CAP}). This is served inline as data-full-options ` +
            "and will bloat page weight; consider a remote-load endpoint.",
        )
      }
    })
  }

  #whenTomSelectsReady(callback) {
    const deadline = performance.now() + 500
    const attempt = () => {
      const allReady = this.levelTargets.every((w) =>
        Boolean(this.#tomSelectFor(w)),
      )
      if (allReady) {
        callback(true)
        return
      }
      if (performance.now() >= deadline) {
        callback(false)
        return
      }
      setTimeout(attempt, 20)
    }
    attempt()
  }

  #filterChildOf(parentIdx) {
    if (this.#cascading) return
    this.#cascading = true
    try {
      this.#filterChildOfImpl(parentIdx)
    } finally {
      this.#cascading = false
    }
  }

  #filterChildOfImpl(parentIdx) {
    const parentTs = this.#tomSelectFor(this.levelTargets[parentIdx])
    const childWrapper = this.levelTargets[parentIdx + 1]
    if (!childWrapper) return
    const childTs = this.#tomSelectFor(childWrapper)
    if (!childTs) return

    const parentValue = parentTs ? String(parentTs.getValue() ?? "") : ""
    const currentChildValue = String(childTs.getValue() ?? "")
    const fullOptions = this.fullOptions[parentIdx + 1] || []

    const filtered = fullOptions.filter((o) => {
      const isLegacy = o.parent_id == null || o.parent_id === ""
      if (isLegacy) {
        // Legacy building (no facilities_site_id, anchored only via
        // contact_company_site mid-backfill) — show ONLY when no parent
        // is picked, matching the strict `buildings_for_filter` on
        // zones/index: legacy buildings surface under "all sites" but
        // never under a specific site (they don't legitimately belong
        // to any FacilitiesSite). Zones + asset_groups schema-guarantee
        // NOT NULL parent columns, so this branch is dead for them.
        return parentValue === ""
      }
      // Non-legacy — require the parent to match. When the parent is
      // unpicked, hide these options so the user is guided top-down
      // through the cascade.
      if (parentValue === "") return false
      return String(o.parent_id) === parentValue
    })

    const stillValid = filtered.some(
      (o) => String(o.value) === currentChildValue,
    )

    // Rebuild Tom Select's option list from the filtered payload.
    //
    // ORDER MATTERS: `clearOptions()` in Tom Select DOES NOT REMOVE the
    // currently-selected option (that's the library's default and even a
    // passing an always-true filter can be inconsistent across versions).
    // So we clear the SELECTION first (silent — no change event), then
    // wipe the option dict, then repopulate with filtered options. This
    // guarantees stale parents' children don't survive a narrower filter.
    childTs.clear(true)
    childTs.clearOptions()
    filtered.forEach((o) => {
      childTs.addOption({ value: String(o.value), text: o.text })
    })
    childTs.refreshOptions(false)

    // Re-apply the previously-selected value only if it survived the
    // filter. If it didn't, we've already cleared it above — the empty
    // state cascades down via direct recursion below.
    if (currentChildValue && stillValid) {
      childTs.setValue(currentChildValue, true)
    }

    // Disable only when there are no meaningful options to choose from:
    // parent unpicked AND no legacy null-parent survivor kept the value
    // live. A legacy (facilities_site_id: nil) building — see the pack's
    // Fork of concerns note — passes the null-parent filter branch and
    // its currentChildValue would otherwise be silently dropped from the
    // form because a disabled <select> is excluded from submission.
    const finalChildValue = String(childTs.getValue() ?? "")
    if (parentValue === "" && finalChildValue === "") {
      childTs.disable()
    } else {
      childTs.enable()
    }

    // Cascade to the next deeper level. Recurse into #filterChildOfImpl
    // directly, NOT #filterChildOf — the re-entry guard is at the outer
    // wrapper and would short-circuit legit recursion.
    if (parentIdx + 1 < this.levelTargets.length - 1) {
      this.#filterChildOfImpl(parentIdx + 1)
    }
  }
}
